import React, { useState, useMemo, useRef } from 'react';
import { guardarConfigCalidad } from '../../servicios/calidadService';
import '../estilos/Calidad.css';

function CheckboxIndeterminate({ checked, indeterminate, onChange, onClick }) {
  const ref = useRef(null);
  useMemo(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      style={{ width: 15, height: 15, accentColor: '#16a34a', flexShrink: 0 }}
    />
  );
}

export default function ConfiguracionCalidad({ actividades, secciones, actividadesActuales, onGuardado, onCerrar }) {
  const [seleccionadas, setSeleccionadas] = useState(new Set(actividadesActuales));
  const [busqueda, setBusqueda] = useState(false);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const mapaSeccion = useMemo(() => {
    const m = {};
    for (const s of secciones) {
      if (s.wbsCode) m[s.wbsCode] = s;
    }
    return m;
  }, [secciones]);

  // Agrupa por sección, y dentro de cada sección agrupa por nombre
  const grupos = useMemo(() => {
    const filtradas = busquedaTexto.trim()
      ? actividades.filter(a => a.nombre?.toLowerCase().includes(busquedaTexto.toLowerCase()))
      : actividades;

    const porSeccion = {};
    for (const act of filtradas) {
      if (!act.wbsCode) continue;
      const nivel1 = act.wbsCode.split('.')[0];
      if (!porSeccion[nivel1]) {
        const sec = mapaSeccion[nivel1];
        porSeccion[nivel1] = { wbs: nivel1, nombre: sec?.nombre || nivel1, orden: sec?.orden ?? 9999, porNombre: {} };
      }
      const nombre = act.nombre || '(sin nombre)';
      if (!porSeccion[nivel1].porNombre[nombre]) porSeccion[nivel1].porNombre[nombre] = [];
      porSeccion[nivel1].porNombre[nombre].push(act);
    }

    return Object.values(porSeccion)
      .sort((a, b) => a.orden - b.orden)
      .map(sec => ({
        ...sec,
        filas: Object.entries(sec.porNombre) // [[nombre, [act, ...]], ...]
      }));
  }, [actividades, mapaSeccion, busquedaTexto]);

  // ── Handlers de selección ──

  const toggleFila = (acts) => {
    const todosSeleccionados = acts.every(a => seleccionadas.has(a.wbsCode));
    setSeleccionadas(prev => {
      const next = new Set(prev);
      for (const a of acts) {
        todosSeleccionados ? next.delete(a.wbsCode) : next.add(a.wbsCode);
      }
      return next;
    });
  };

  const toggleGrupo = (grupo) => {
    const todasActs = grupo.filas.flatMap(([, acts]) => acts);
    const todosSeleccionados = todasActs.every(a => seleccionadas.has(a.wbsCode));
    setSeleccionadas(prev => {
      const next = new Set(prev);
      for (const a of todasActs) {
        todosSeleccionados ? next.delete(a.wbsCode) : next.add(a.wbsCode);
      }
      return next;
    });
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const nuevas = await guardarConfigCalidad([...seleccionadas]);
      onGuardado(nuevas);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const totalPartidas = useMemo(
    () => grupos.reduce((sum, g) => sum + g.filas.length, 0),
    [grupos]
  );

  return (
    <div className="calidad-modal-overlay" onClick={onCerrar}>
      <div className="calidad-modal" onClick={e => e.stopPropagation()}>
        <div className="calidad-modal-header">
          <h3>Seleccionar partidas de Calidad</h3>
          <p>
            Elige las partidas del programa a las que podrás registrar avance.
            Las que tienen el mismo nombre están agrupadas — un clic las selecciona todas.
          </p>
        </div>

        <div style={{ padding: '0 24px 8px' }}>
          <input
            type="text"
            placeholder="Buscar partida…"
            value={busquedaTexto}
            onChange={e => { setBusquedaTexto(e.target.value); setBusqueda(true); }}
            style={{
              width: '100%', boxSizing: 'border-box',
              border: '1px solid #e5e7eb', borderRadius: 6,
              padding: '7px 12px', fontSize: 13, outline: 'none'
            }}
          />
        </div>

        <div className="calidad-modal-body">
          {grupos.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
              {busqueda && busquedaTexto ? 'Sin resultados para esa búsqueda.' : 'No hay actividades disponibles en el programa activo.'}
            </p>
          ) : (
            grupos.map(grupo => {
              const todasActs = grupo.filas.flatMap(([, acts]) => acts);
              const todosGrupo = todasActs.every(a => seleccionadas.has(a.wbsCode));
              const algunoGrupo = todasActs.some(a => seleccionadas.has(a.wbsCode));
              const countSelGrupo = todasActs.filter(a => seleccionadas.has(a.wbsCode)).length;

              return (
                <div key={grupo.wbs} style={{ marginBottom: 12 }}>
                  {/* Encabezado de sección */}
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px', background: '#f3f4f6',
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      border: '1px solid #e5e7eb'
                    }}
                    onClick={() => toggleGrupo(grupo)}
                  >
                    <CheckboxIndeterminate
                      checked={todosGrupo}
                      indeterminate={algunoGrupo && !todosGrupo}
                      onChange={() => toggleGrupo(grupo)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', flex: 1 }}>
                      {grupo.nombre}
                    </span>
                    {countSelGrupo > 0 ? (
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: '#15803d',
                        background: '#dcfce7', borderRadius: 10, padding: '1px 7px'
                      }}>
                        {countSelGrupo}/{todasActs.length}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {grupo.filas.length} tipo{grupo.filas.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Filas agrupadas por nombre */}
                  <div style={{ paddingLeft: 12 }}>
                    {grupo.filas.map(([nombre, acts]) => {
                      const todosFila = acts.every(a => seleccionadas.has(a.wbsCode));
                      const algunoFila = acts.some(a => seleccionadas.has(a.wbsCode));
                      const sel = todosFila;

                      return (
                        <div
                          key={nombre}
                          className={`calidad-seccion-opcion ${sel ? 'seleccionada' : ''}`}
                          style={{ marginBottom: 4 }}
                          onClick={() => toggleFila(acts)}
                        >
                          <CheckboxIndeterminate
                            checked={todosFila}
                            indeterminate={algunoFila && !todosFila}
                            onChange={() => toggleFila(acts)}
                            onClick={e => e.stopPropagation()}
                          />
                          <span className="calidad-seccion-opcion-label">{nombre}</span>
                          {acts.length > 1 && (
                            <span style={{
                              fontSize: 11, fontWeight: 700, color: '#6b7280',
                              background: '#f3f4f6', borderRadius: 10,
                              padding: '1px 7px', marginLeft: 4, whiteSpace: 'nowrap'
                            }}>
                              ×{acts.length}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
          {error && (
            <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{error}</p>
          )}
        </div>

        <div className="calidad-modal-footer">
          <span style={{ fontSize: 12, color: '#6b7280', marginRight: 'auto' }}>
            {seleccionadas.size} actividad{seleccionadas.size !== 1 ? 'es' : ''} seleccionada{seleccionadas.size !== 1 ? 's' : ''}
            {totalPartidas > 0 && (
              <span style={{ color: '#9ca3af' }}> ({totalPartidas} tipos únicos)</span>
            )}
          </span>
          <button className="calidad-btn-secondary" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </button>
          <button className="calidad-btn-primary" onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
